import { ThemeEvents } from '@theme/events';
import { StandardEvents } from '@shopify/events';

class MaknortePdpBundle extends HTMLElement {
  #eventTarget;
  #abortController;
  #basePrice = NaN;
  #baseVariant = 0;
  #baseState = 'unavailable';
  #variantGeneration = 0;
  #addPending = false;

  connectedCallback() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#eventTarget = this.closest('.shopify-section, dialog');
    this.#basePrice = this.#integer(this.dataset.basePrice, 0) ?? NaN;
    this.#baseVariant = this.#integer(this.dataset.baseVariant, 1) ?? 0;
    this.#baseState = this.dataset.baseAvailable === 'true' && this.#validBase() ? 'ready' : 'unavailable';

    for (const item of this.#items) {
      item.querySelector('[data-mv2-check]')?.addEventListener('click', () => this.#toggleItem(item), { signal });
    }
    this.#addButton?.addEventListener('click', this.#addSelected, { signal });
    this.#eventTarget?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.#eventTarget?.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    this.#eventTarget?.addEventListener('change', this.#handleQuantityChange, { signal });

    this.#recalculate();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#variantGeneration += 1;
  }

  get #items() {
    return Array.from(this.querySelectorAll('[data-mv2-bundle-item]'));
  }

  get #selectedItems() {
    return this.#items.filter((item) => item.querySelector('[data-mv2-check]')?.getAttribute('aria-pressed') === 'true');
  }

  get #count() {
    return this.querySelector('[data-mv2-bundle-count]');
  }

  get #total() {
    return this.querySelector('[data-mv2-bundle-total]');
  }

  get #addButton() {
    return this.querySelector('[data-mv2-bundle-add]');
  }

  #validBase() {
    return Number.isSafeInteger(this.#basePrice) && this.#basePrice >= 0 && Number.isSafeInteger(this.#baseVariant) && this.#baseVariant > 0;
  }

  #integer(value, minimum) {
    if ((typeof value !== 'string' && typeof value !== 'number') || (typeof value === 'string' && value.trim() === '')) return null;
    const parsed = Number(value);
    return Number.isSafeInteger(parsed) && parsed >= minimum ? parsed : null;
  }

  #productForm() {
    return this.#eventTarget?.querySelector('product-form-component form[action*="/cart/add"], form[action*="/cart/add"][id^="product-form"], form[action*="/cart/add"]') ?? null;
  }

  #quantity() {
    const input = this.#productForm()?.querySelector('input[name="quantity"]');
    if (!input || (typeof input.checkValidity === 'function' && !input.checkValidity())) return null;
    return this.#integer(input.value, 1);
  }

  #setBaseState(state) {
    this.#baseState = state;
    if (state !== 'ready') this.dataset.baseAvailable = 'false';
    this.#renderBlocked(state);
  }

  #renderBlocked(state) {
    const total = this.#total;
    if (total) {
      total.textContent = state === 'pending' ? 'Atualizando…' : 'Total indisponível';
      if (state === 'pending') total.setAttribute('aria-busy', 'true');
      else total.removeAttribute('aria-busy');
    }
    if (this.#count) this.#count.textContent = String(this.#selectedItems.length + (state === 'ready' ? 1 : 0));
    if (this.#addButton) this.#addButton.disabled = true;
  }

  #recalculate() {
    if (this.#baseState !== 'ready' || !this.#validBase()) {
      this.#setBaseState(this.#baseState === 'pending' ? 'pending' : 'unavailable');
      return;
    }

    const selection = this.#selection();
    if (!selection) {
      this.#renderBlocked('unavailable');
      return;
    }
    const { selected, quantity, accessoryTotal } = selection;
    const totalPrice = this.#basePrice * quantity + accessoryTotal;
    if (!Number.isSafeInteger(totalPrice)) {
      this.#renderBlocked('unavailable');
      return;
    }
    if (this.#count) this.#count.textContent = String(selected.length + 1);
    if (this.#total) {
      this.#total.textContent = this.#money(totalPrice);
      this.#total.removeAttribute('aria-busy');
    }
    if (this.#addButton) this.#addButton.disabled = this.#addPending;
  }

  #selection() {
    const quantity = this.#quantity();
    if (quantity === null) return null;
    const selected = this.#selectedItems;
    let accessoryTotal = 0;
    const accessories = [];
    for (const item of selected) {
      const price = this.#integer(item.dataset.price, 0);
      const variant = this.#integer(item.dataset.variant, 1);
      if (price === null || variant === null) return null;
      accessoryTotal += price;
      if (!Number.isSafeInteger(accessoryTotal)) return null;
      accessories.push({ id: variant, quantity: 1 });
    }
    return { selected, quantity, accessoryTotal, accessories };
  }

  #money(cents) {
    if (typeof window.MNV2Api?.money === 'function') return window.MNV2Api.money(cents);
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(cents) / 100);
  }

  #publishBundleAdd(count) {
    const eventId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    try {
      document.dispatchEvent(new CustomEvent('mv2:track', {
        detail: {
          name: 'mn_bundle_add',
          payload: { count },
          eventId,
          emitter_id: 'maknorte-theme-v2',
          schema_version: 1,
        },
      }));
    } catch (error) {
      // Analytics must never block the completed cart operation.
    }
  }

  #toggleItem(item) {
    const check = item.querySelector('[data-mv2-check]');
    if (!check) return;
    const selected = check.getAttribute('aria-pressed') !== 'true';
    check.setAttribute('aria-pressed', selected ? 'true' : 'false');
    item.classList.toggle('mv2-bundle__item--off', !selected);
    this.#recalculate();
  }

  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card') || !event.promise) return;

    const generation = ++this.#variantGeneration;
    this.#basePrice = NaN;
    this.#baseVariant = 0;
    this.#setBaseState('pending');

    event.promise
      .then(({ detail }) => {
        if (generation !== this.#variantGeneration) return;
        if (!detail?.html || (!detail.newProduct && detail.productId && detail.productId !== this.dataset.productId)) {
          this.#setBaseState('unavailable');
          return;
        }

        if (detail.newProduct) {
          this.#setBaseState('unavailable');
          return;
        }

        const resource = detail.resource;
        const price = this.#integer(resource?.price, 0);
        const variant = this.#integer(resource?.id, 1);
        if (!resource || resource.available !== true || price === null || variant === null) {
          this.#setBaseState('unavailable');
          return;
        }

        this.#basePrice = price;
        this.#baseVariant = variant;
        this.dataset.basePrice = String(price);
        this.dataset.baseVariant = String(variant);
        this.dataset.baseAvailable = 'true';
        this.#baseState = 'ready';
        this.#recalculate();
      })
      .catch((error) => {
        if (generation !== this.#variantGeneration) return;
        this.#setBaseState('unavailable');
        if (error?.name !== 'AbortError') console.warn('[mn-pdp-bundle] Variant update failed:', error);
      });
  };

  #handleQuantityUpdate = (event) => {
    if (!this.#isMainProductFormEvent(event)) return;
    this.#recalculate();
  };

  #handleQuantityChange = (event) => {
    if (event.target?.name !== 'quantity' || !this.#isMainProductFormEvent(event)) return;
    this.#recalculate();
  };

  #isMainProductFormEvent(event) {
    const mainForm = this.#productForm();
    const eventForm = event.target?.closest?.('form[action*="/cart/add"]');
    return !mainForm || !eventForm || eventForm === mainForm;
  }

  #addSelected = async () => {
    const selection = this.#selection();
    if (this.#addPending || this.#baseState !== 'ready' || !this.#validBase() || !selection) {
      window.MNV2Api?.toast?.('Selecione uma variante disponível.');
      return;
    }

    const items = [{ id: this.#baseVariant, quantity: selection.quantity }].concat(selection.accessories);
    const button = this.#addButton;
    this.#addPending = true;
    if (button) button.disabled = true;
    try {
      if (typeof window.MNV2Api?.cartAdd !== 'function') throw new Error('Não foi possível adicionar. Tente novamente.');
      await window.MNV2Api.cartAdd(items, 'bundle');
      this.#publishBundleAdd(items.length);
      window.MNV2Api.toast?.(`${items.length} itens adicionados ao carrinho.`, 'Ver carrinho', window.MNV2?.cartUrl || '/cart');
    } catch (error) {
      window.MNV2Api?.toast?.(error.message);
    } finally {
      this.#addPending = false;
      this.#recalculate();
    }
  };
}

if (!customElements.get('maknorte-pdp-bundle')) {
  customElements.define('maknorte-pdp-bundle', MaknortePdpBundle);
}
